import { useState } from 'react';
import { X, Trash2, Calendar, ChevronDown, ChevronUp, BookOpen, Lightbulb } from 'lucide-react';
import MathText from '../../VueQuestion/MathText';

// Configuration des types d'activités
const ACTIVITY_CONFIGS = {
  AUTOMATISME: {
    icon: "⚡",
    bg: "bg-emerald-500/10",
    bgSolid: "bg-emerald-500",
    gradient: "from-emerald-500 via-emerald-600 to-teal-500",
    border: "border-emerald-200",
    text: "text-emerald-600",
    textDark: "text-emerald-700",
    label: "Automatisme",
    labelPlural: "Automatismes"
  },
  RESOLUTION: {
    icon: "🧩",
    bg: "bg-rose-500/10",
    bgSolid: "bg-rose-500",
    gradient: "from-rose-500 via-rose-600 to-pink-500",
    border: "border-rose-200",
    text: "text-rose-600",
    textDark: "text-rose-700",
    label: "Résolution",
    labelPlural: "Résolutions de problèmes"
  },
  QFLASH: {
    icon: "💡",
    bg: "bg-amber-500/10",
    bgSolid: "bg-amber-500",
    gradient: "from-amber-500 via-amber-600 to-orange-500",
    border: "border-amber-200",
    text: "text-amber-600",
    textDark: "text-amber-700",
    label: "QFlash",
    labelPlural: "QFlash"
  },
};

// Composant pour une section collapsible (Attendus ou Savoirs-faire)
function CollapsibleSection({ title, items, icon: Icon, bgColor, textColor, renderItem }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxVisible = 2;
  const hasMore = items.length > maxVisible;
  const visibleItems = isExpanded ? items : items.slice(0, maxVisible);

  return (
    <div className="mt-2 sm:mt-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${textColor}`} />
        <p className={`text-[10px] sm:text-xs font-semibold ${textColor}`}>
          {title}
        </p>
        <span className={`text-[9px] sm:text-[10px] ${textColor} opacity-70`}>
          ({items.length})
        </span>
      </div>

      <div className="space-y-1">
        {visibleItems.map((item, idx) => (
          <div
            key={item.id || idx}
            className={`px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg ${bgColor} border border-opacity-20`}
          >
            <p className={`text-[10px] sm:text-xs ${textColor} font-medium leading-relaxed`}>
              {renderItem(item)}
            </p>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`mt-1.5 flex items-center gap-1 text-[10px] sm:text-xs ${textColor} hover:opacity-80 transition-opacity`}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              <span>Voir moins</span>
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              <span>Voir plus ({items.length - maxVisible})</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

// Composant pour une carte d'activité
function ActivityCard({ activity, config, onDelete, formatDate }) {
  return (
    <div className="group relative">
      <div
        className={`
          relative p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 ${config.border}
          bg-gradient-to-br from-white to-slate-50
          shadow-sm hover:shadow-lg transition-all duration-300
          hover:border-opacity-60
        `}
      >
        {/* Header avec titre et bouton supprimer */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Badge type */}
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${config.bg} mb-2`}>
              <span className="text-xs">{config.icon}</span>
              <span className={`text-[9px] sm:text-[10px] font-semibold ${config.text}`}>
                {config.label}
              </span>
            </div>

            {/* Titre */}
            <h4 className="font-bold text-sm sm:text-base text-slate-800 leading-tight">
              {activity.title}
            </h4>

            {/* Dates */}
            {activity.start && activity.end && (
              <div className="flex items-center gap-1.5 mt-2">
                <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400" />
                <p className="text-[10px] sm:text-xs text-slate-500 font-medium">
                  {formatDate(activity.start)} - {formatDate(activity.end)}
                </p>
              </div>
            )}
          </div>

          {/* Bouton supprimer - visible au hover */}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(activity);
              }}
              className="opacity-0 group-hover:opacity-100 p-1.5 sm:p-2 rounded-lg
                bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600
                transition-all duration-200 hover:scale-110 shadow-sm"
              title="Supprimer l'activité"
            >
              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
          
        </div>

        {/* Sections Attendus et Savoirs-faire */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          {activity.attendus_details?.length > 0 && (
            <CollapsibleSection
              title="Attendus"
              items={activity.attendus_details}
              icon={BookOpen}
              bgColor="bg-blue-50"
              textColor="text-blue-600"
              renderItem={(attendu) => <MathText>{attendu.description}</MathText>}
            />
          )}

          {activity.savoir_faires_details?.length > 0 && (
            <CollapsibleSection
              title="Savoirs-faire"
              items={activity.savoir_faires_details}
              icon={Lightbulb}
              bgColor="bg-emerald-50"
              textColor="text-emerald-600"
              renderItem={(sf) => <MathText>{sf.nom || sf.description}</MathText>}
            />
          )}

          {/* Message si aucun attendu ni savoir-faire */}
          {(!activity.attendus_details?.length && !activity.savoir_faires_details?.length) && (
            <p className="text-[10px] sm:text-xs text-slate-400 italic">
              Aucun attendu ou savoir-faire associé
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityListModal({ isOpen, onClose, type, activities, sequenceTitle, onDeleteActivity }) {
  if (!isOpen) return null;

  const config = ACTIVITY_CONFIGS[type] || ACTIVITY_CONFIGS.AUTOMATISME;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg max-h-[95vh] sm:max-h-[85vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="relative overflow-hidden flex-shrink-0">
          <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient}`} />

          {/* Animated background pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 100" preserveAspectRatio="none">
            <path fill="white">
              <animate attributeName="d" dur="4s" repeatCount="indefinite"
                values="M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,80 200,50 Q300,20 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z" />
            </path>
          </svg>

          <div className="relative px-3 py-3 sm:px-5 sm:py-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0">
                <span className="text-xl sm:text-2xl">{config.icon}</span>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-bold text-white truncate">
                  {config.labelPlural}
                </h2>
                <p className="text-white/80 text-xs sm:text-sm truncate">
                  {sequenceTitle}
                </p>
                <p className="text-white/60 text-[10px] sm:text-xs">
                  {activities?.length || 0} activité{(activities?.length || 0) > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Liste des activités */}
        <div className="p-3 sm:p-4 overflow-y-auto flex-1 min-h-0">
          {activities && activities.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {activities.map((activity, idx) => (
                <ActivityCard
                  key={activity.id || idx}
                  activity={activity}
                  config={config}
                  onDelete={onDeleteActivity}
                  formatDate={formatDate}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12">
              <div className={`w-16 h-16 sm:w-20 sm:h-20 ${config.bg} rounded-2xl flex items-center justify-center mb-4`}>
                <span className="text-3xl sm:text-4xl">{config.icon}</span>
              </div>
              <p className="text-slate-500 text-sm sm:text-base font-medium">Aucune activité</p>
              <p className="text-slate-400 text-xs sm:text-sm mt-1">
                Pas d'activité spiralée associée
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 sm:px-5 py-2.5 sm:py-3
              bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700
              text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base
              transition-all duration-200
              shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40
              hover:-translate-y-0.5"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActivityListModal;
